import { AgentService } from '@/services';
import { ENDPOINT } from '@/services/endpoint';
import { IResponseError } from '@/types/common';
import { AgentEngineId } from '@/types/response';
import { notificationMessage, NOTIFY_STATUS } from '@/utils';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  DatePicker,
  Form,
  InputNumber,
  Popconfirm,
  Select,
  Space,
  Spin,
  Steps,
  Tag,
  Typography,
} from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useMutation, useQuery } from 'react-query';
import { useSearchParams } from 'react-router-dom';
import { ENGINE_TAG_COLOR, GenerationLogView } from '../agentShared';

const { Title, Paragraph, Text } = Typography;

interface IProps {
  title?: string;
}

export const AgentRun: React.FC<IProps> = ({ title }) => {
  // 家族は id 直接入力で指定する（家族一覧ページの「実行」ボタンからは
  // ?familyId= で引き継がれる）。
  // 入力途中の中間値（1→12→123）でメンバー取得が走らないよう 500ms デバウンス
  const [searchParams] = useSearchParams();
  const initialFamilyId = (() => {
    const v = parseInt(searchParams.get('familyId') ?? '', 10);
    return Number.isInteger(v) && v > 0 ? v : null;
  })();
  const [familyIdInput, setFamilyIdInput] = useState<number | null>(initialFamilyId);
  const [familyId, setFamilyId] = useState<number | null>(initialFamilyId);
  const [engineId, setEngineId] = useState<AgentEngineId | null>(null);
  const [targetProfileId, setTargetProfileId] = useState<number | null>(null);
  const [date, setDate] = useState<Dayjs | null>(dayjs());
  const [post, setPost] = useState<boolean>(false);
  const [jobId, setJobId] = useState<number | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setFamilyId(familyIdInput);
      setTargetProfileId(null);
    }, 500);
    return () => clearTimeout(timer);
  }, [familyIdInput]);

  const { data: enginesData } = useQuery(
    [ENDPOINT.AGENT.LIST_ENGINES],
    () => AgentService.listEngines(),
    { staleTime: Infinity },
  );
  const engines = enginesData?.data ?? [];

  const { data: membersData, isLoading: membersLoading } = useQuery(
    [ENDPOINT.AGENT.LIST_FAMILY_MEMBERS, familyId],
    () => AgentService.listFamilyMembers(familyId!),
    { enabled: !!familyId },
  );
  const family = membersData?.data.family ?? null;
  const members = membersData?.data.members ?? [];
  const familyNotFound = !!familyId && !!membersData && !family;

  const runMutation = useMutation(
    () =>
      AgentService.startRun({
        familyId: familyId!,
        engineId: engineId!,
        publishDate: date!.format('YYYY-MM-DD'),
        targetProfileId: targetProfileId ?? undefined,
        post,
      }),
    {
      onSuccess: (response) => {
        setJobId(response.data.jobId);
        notificationMessage({
          message:
            '実行を開始しました。完了までこのページでお待ちください（数十秒かかります）',
          type: NOTIFY_STATUS.SUCCESS,
        });
      },
      onError: (error: IResponseError) => {
        notificationMessage({
          message: `実行の開始に失敗しました: ${error.message ?? 'unknown error'}`,
          type: NOTIFY_STATUS.ERROR,
        });
      },
    },
  );

  // ジョブをポーリング（pending/running の間は 3 秒ごと）
  const { data: jobData } = useQuery(
    [ENDPOINT.AGENT.RUN_JOB, jobId],
    () => AgentService.getRunJob(jobId!),
    {
      enabled: !!jobId,
      refetchInterval: (resp) => {
        const s = resp?.data.status;
        return s === 'pending' || s === 'running' ? 3000 : false;
      },
    },
  );
  const job = jobData?.data;
  const isJobRunning = job?.status === 'pending' || job?.status === 'running';
  const resultMessageId =
    job?.status === 'done' ? job.result?.agentMessageId ?? null : null;

  // 完了後に本文と生成ログを取得
  const { data: messageData } = useQuery(
    [ENDPOINT.AGENT.GET_MESSAGE, resultMessageId],
    () => AgentService.getMessage(resultMessageId!),
    { enabled: !!resultMessageId },
  );
  const { data: logData } = useQuery(
    [ENDPOINT.AGENT.GET_MESSAGE_GENERATION_LOG, resultMessageId],
    () => AgentService.getMessageGenerationLog(resultMessageId!),
    { enabled: !!resultMessageId },
  );
  const resultMessage = messageData?.data;

  const nudgeMutation = useMutation(
    () => AgentService.runProfileNudge(familyId!),
    {
      onError: (error: IResponseError) => {
        notificationMessage({
          message: `nudge の送信に失敗しました: ${error.message ?? 'unknown error'}`,
          type: NOTIFY_STATUS.ERROR,
        });
      },
    },
  );
  // 家族IDを変えた後に前の家族の結果表示が残らないよう、現在の家族の結果だけ表示
  const isCurrentFamilyNudge = nudgeMutation.data?.data.familyId === familyId;
  const nudgeResult = isCurrentFamilyNudge
    ? nudgeMutation.data?.data.result ?? null
    : null;
  const nudgeKind = isCurrentFamilyNudge
    ? nudgeMutation.data?.data.kind ?? null
    : null;

  const handleRun = () => {
    if (!familyId || !engineId || !date) {
      notificationMessage({
        message: '家族ID・エンジン・対象日を指定してください',
        type: NOTIFY_STATUS.WARNING,
      });
      return;
    }
    if (!family) {
      notificationMessage({
        message: `id=${familyId} の家族が確認できません。家族IDを確認してください`,
        type: NOTIFY_STATUS.WARNING,
      });
      return;
    }
    runMutation.mutate();
  };

  const engineLabel = (id: AgentEngineId) =>
    engines.find((e) => e.id === id)?.label ?? id;

  // 実行時点の検索情報しか取れないエンジンは当日のみ実行可
  // （日付指定対応は outing / worry / shocho。サーバー側でも検証される）
  const TODAY_ONLY_ENGINES: AgentEngineId[] = ['kinjo', 'otoku', 'kigen'];
  const isTodayOnly = !!engineId && TODAY_ONLY_ENGINES.includes(engineId);

  return (
    <div>
      <Helmet>
        <title>{title}</title>
      </Helmet>
      <div className='flex flex-col gap-[16px] mx-auto w-full max-w-[1280px] h-full'>
        <header>
          <Title level={3} style={{ marginTop: 0 }}>
            AIエージェント（フクマル）手動実行
          </Title>
          <Paragraph type='secondary' style={{ marginBottom: 16 }}>
            指定した家族に対して特定のエンジンを実行します。地域は対象メンバー
            （未指定なら最年長メンバー）の住所（郵便番号）を基準にします。
            「家族トークに投稿」をオフにすると生成のみ行い、トークには流れません（品質確認用）。
            各エンジンの仕組み（プロンプト・検索構成）は「フロー解説」ページを参照してください。
          </Paragraph>
        </header>

        <Card>
          <Form layout='inline'>
            <Form.Item label='家族ID' required>
              <Space direction='vertical' size={2}>
                <InputNumber
                  style={{ width: 140 }}
                  min={1}
                  placeholder='家族ID'
                  value={familyIdInput}
                  onChange={(v) => setFamilyIdInput(v ?? null)}
                />
                {family && (
                  <Text type='success' style={{ fontSize: 12 }}>
                    ✓ {family.name}（メンバー{members.length}人）
                  </Text>
                )}
                {familyNotFound && (
                  <Text type='danger' style={{ fontSize: 12 }}>
                    id={familyId} の家族が見つかりません
                  </Text>
                )}
              </Space>
            </Form.Item>
            <Form.Item label='エンジン' required>
              <Select
                style={{ width: 220 }}
                placeholder='エンジンを選択'
                value={engineId}
                onChange={(v) => {
                  setEngineId(v);
                  if (v && TODAY_ONLY_ENGINES.includes(v)) {
                    setDate(dayjs());
                  }
                }}
                options={engines.map((e) => ({
                  value: e.id,
                  label: `${e.label}（${e.id}）`,
                }))}
              />
            </Form.Item>
            <Form.Item label='対象メンバー'>
              <Select
                style={{ width: 280 }}
                allowClear
                loading={membersLoading}
                disabled={!familyId}
                placeholder='未指定＝最年長を自動解決'
                value={targetProfileId}
                onChange={(v) => setTargetProfileId(v ?? null)}
                options={members.map((m) => ({
                  value: m.profileId,
                  label: `${m.nickname}${m.dayOfBirth ? ` (${m.dayOfBirth})` : ''}${m.address ? '' : ' ⚠️住所なし'}`,
                }))}
              />
            </Form.Item>
            <Form.Item label='対象日' required>
              <DatePicker
                value={date}
                onChange={(value) => setDate(value)}
                format='YYYY-MM-DD'
                allowClear={false}
                disabledDate={(d) => isTodayOnly && !d.isSame(dayjs(), 'day')}
              />
              {isTodayOnly && (
                <div>
                  <Text type='secondary' style={{ fontSize: 12 }}>
                    このエンジンは当日のみ実行できます（日付指定は outing / worry / shocho のみ）
                  </Text>
                </div>
              )}
            </Form.Item>
            <Form.Item>
              <Checkbox
                checked={post}
                onChange={(e) => setPost(e.target.checked)}
              >
                家族トークに投稿する（push通知あり）
              </Checkbox>
            </Form.Item>
            <Form.Item>
              <Button
                type='primary'
                onClick={handleRun}
                loading={runMutation.isLoading || isJobRunning}
                disabled={isJobRunning}
              >
                {isJobRunning ? '実行中…' : '実行する'}
              </Button>
            </Form.Item>
          </Form>
          {post && (
            <Alert
              style={{ marginTop: 12 }}
              type='warning'
              showIcon
              message='実際の家族トークにフクマルの投稿が流れ、家族全員に push 通知が届きます。テスト用家族であることを確認してください。'
            />
          )}
        </Card>

        {job && isJobRunning && (
          <Card>
            <Space direction='vertical' style={{ width: '100%' }} size='middle'>
              <Space>
                <Spin />
                <Text>
                  実行中です（{job.publishDate} /{' '}
                  {engineLabel(job.engineId)}）。完了までこのページでお待ちください…
                </Text>
              </Space>
              {job.progress && job.progress.steps.length > 0 && (
                <Steps
                  size='small'
                  direction='vertical'
                  items={job.progress.steps.map((s) => ({
                    title: s.label,
                    status:
                      s.status === 'done'
                        ? 'finish'
                        : s.status === 'running'
                          ? 'process'
                          : s.status === 'failed'
                            ? 'error'
                            : 'wait',
                    description:
                      s.status === 'running'
                        ? '処理中…'
                        : s.status === 'failed'
                          ? '失敗'
                          : undefined,
                  }))}
                />
              )}
            </Space>
          </Card>
        )}

        {job && job.status === 'failed' && (
          <Alert
            type='error'
            showIcon
            message='実行ジョブが失敗しました'
            description={job.error ?? undefined}
          />
        )}

        {job && job.status === 'done' && job.result?.skipped && (
          <Alert
            type='info'
            showIcon
            message='今日は発火なし'
            description={job.result.skipReason ?? '今日のファクトがないため投稿しません（正常）'}
          />
        )}

        {job && job.status === 'done' && job.result && !job.result.skipped && (
          <Card
            title={
              <Space>
                <span>結果</span>
                <Tag color={ENGINE_TAG_COLOR[job.result.engineId]}>
                  {engineLabel(job.result.engineId)}
                </Tag>
                <Tag color={job.result.posted ? 'green' : 'default'}>
                  {job.result.posted ? 'トーク投稿済み' : '生成のみ（未投稿）'}
                </Tag>
                <Text type='secondary' style={{ fontSize: 12 }}>
                  message id={job.result.agentMessageId}
                </Text>
              </Space>
            }
          >
            <Space direction='vertical' style={{ width: '100%' }} size='middle'>
              <pre
                style={{
                  background: '#f6ffed',
                  padding: 16,
                  borderRadius: 4,
                  fontSize: 13,
                  whiteSpace: 'pre-wrap',
                  margin: 0,
                }}
              >
                {resultMessage?.text ?? job.result.textPreview}
              </pre>
              {resultMessage?.usage && (
                <Text type='secondary' style={{ fontSize: 12 }}>
                  {resultMessage.usage.model} / {resultMessage.usage.provider} /
                  in: {resultMessage.usage.input_tokens} / out:{' '}
                  {resultMessage.usage.output_tokens}
                </Text>
              )}
              <GenerationLogView log={logData?.data.generationLog ?? null} />
            </Space>
          </Card>
        )}

        <Card title='フクマル nudge（テスト送信）'>
          <Space direction='vertical' style={{ width: '100%' }} size='middle'>
            <Paragraph type='secondary' style={{ margin: 0 }}>
              上の「家族ID」で指定した家族の家族トークに、フクマルから nudge
              メッセージを投稿します（本文に個人名は出ません）。 種別は
              <b>メンバー1人だけの家族＝家族の招待促進</b>、
              <b>2名以上＝誕生日・郵便番号の登録促進</b> を自動判定します。
              週次バッチと同じ判定を通るため、未登録メンバーがいない場合や
              直近6日以内に同じ種別を送信済みの場合はスキップされます。
            </Paragraph>
            <Popconfirm
              title='実際の家族トークに投稿され、家族全員に push 通知が届きます。送信しますか？'
              okText='送信する'
              cancelText='キャンセル'
              onConfirm={() => nudgeMutation.mutate()}
              disabled={!family}
            >
              <Button
                danger
                loading={nudgeMutation.isLoading}
                disabled={!family}
              >
                nudge を送信する
              </Button>
            </Popconfirm>
            {!family && (
              <Text type='secondary' style={{ fontSize: 12 }}>
                上の「家族ID」で家族を指定すると送信できます
              </Text>
            )}
            {nudgeResult === 'sent' && (
              <Alert
                type='success'
                showIcon
                message={
                  nudgeKind === 'familyInviteNudge'
                    ? '送信しました（家族の招待促進）'
                    : '送信しました（プロフィール登録促進）'
                }
                description={
                  nudgeKind === 'familyInviteNudge'
                    ? '家族トークにフクマルの招待促進メッセージを投稿し、push 通知を送りました。'
                    : '家族トークにフクマルの登録促進メッセージを投稿し、push 通知を送りました。'
                }
              />
            )}
            {nudgeResult === 'skipped_complete' && (
              <Alert
                type='info'
                showIcon
                message='スキップ: 全員登録済み'
                description='この家族には誕生日・郵便番号が未登録のメンバーがいないため送信しません。'
              />
            )}
            {nudgeResult === 'skipped_recent' && (
              <Alert
                type='info'
                showIcon
                message='スキップ: 直近送信済み'
                description='この家族には直近6日以内に同じ種別を送信済みのため送信しません（二重送信ガード）。'
              />
            )}
            {nudgeResult === 'skipped_no_member' && (
              <Alert
                type='warning'
                showIcon
                message='スキップ: メンバーが取得できません'
                description='この家族には ONBOARD 状態のメンバー（プロフィール）が見つかりません。'
              />
            )}
          </Space>
        </Card>

        <Paragraph type='secondary'>
          過去の生成結果は「生成一覧」ページで確認できます。
        </Paragraph>
      </div>
    </div>
  );
};
